import { Trans, useLingui } from '@lingui/react/macro'
import { CantileverButton } from 'components/CantileverButton'
import { useGeckoPermissions } from 'features/permissions'
import { AgentTab } from 'features/timeSeries/agentTab/AgentTab'
import { useModelBuilderAccess } from 'features/timeSeries/agentTab/modelBuilderAccess'
import { AllTagsTab } from 'features/timeSeries/components/AllTagsTab'
import { ChartTooltip } from 'features/timeSeries/components/ChartTooltip'
import { LabelsTab } from 'features/timeSeries/components/LabelsTab'
import { PensTab } from 'features/timeSeries/components/PensTab'
import { ResizeHandleComponent } from 'features/timeSeries/components/ResizeHandleComponent'
import { SentinelsTab } from 'features/timeSeries/components/SentinelsTab'
import { CHART_COLORS } from 'features/timeSeries/config/constants'
import { ExperimentTab } from 'features/timeSeries/experiment/ExperimentTab'
import { X } from 'lucide-react'
import React, { useRef, useState } from 'react'
import styled from 'styled-components'
import { BaseSensorEntry, PERMISSION_GECKO_INTERNAL_MEMBER } from 'types'

const StyledX = styled(X)`
  width: 1.25rem;
  height: 1.25rem;
`

const SidebarContainer = styled.div<{
  $width?: number
}>`
  display: flex;
  flex-direction: column;
  min-height: 0;
  width: ${({ $width }) => ($width ? `${$width}%` : '50%')};
  min-width: 16rem;
  background: ${CHART_COLORS.BACKGROUND};
  border: 1px solid ${({ theme }) => theme.forecast.cardDividerBackground};
  border-radius: 0.375rem;
  margin: 0.25rem;
  position: relative;
  z-index: 1;
  container-type: inline-size;

  /* In a tight chart area, a 50% split squishes both the config panel and the
     chart. Overlay the full area instead so the panel keeps a usable width. The
     bigger (wide) view is unaffected and stays side-by-side. */
  @container chartArea (max-width: 44rem) {
    position: absolute;
    inset: 0.25rem;
    width: auto;
    min-width: 0;
    margin: 0;
    z-index: 5;
  }
`

const SidebarInner = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  border-radius: 0.375rem;
`

const SidebarContent = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  padding: 0.125rem;
  flex: 1;
`

/*
 * The close button sits in the TOP-LEFT corner: the drawer docks on the right
 * of the chart, so the close affordance belongs on the corner nearest the
 * chart. The title stays absolutely centered.
 */
const SidebarHeader = styled.header`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  position: relative;
  padding-bottom: 0.125rem;
`

const SidebarTitle = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
`

const TabStrip = styled.div`
  display: flex;
  gap: 0.25rem;
  padding: 0 0.25rem;
  border-bottom: 0.25rem solid
    ${({ theme }) => theme.forecast.cardDividerBackground};
`

const Tab = styled.button<{ $isSelected: boolean }>`
  padding: 0.375rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme, $isSelected }) =>
    $isSelected ? theme.forecast.activeText : theme.forecast.secondaryText};
  background: none;
  border: none;
  border-bottom: 2px solid
    ${({ theme, $isSelected }) =>
      $isSelected ? theme.forecast.activeText : 'transparent'};
  margin-bottom: -0.25rem;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.forecast.activeText};
  }
`

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  padding-top: 0.125rem;
  min-height: 0;

  /* Compact table styling within the sidebar */
  thead {
    height: auto !important;
  }

  th {
    padding: 0 !important;
    font-size: 0.6875rem;
  }

  th > div {
    padding: 0.125rem 0.25rem !important;
  }

  td {
    min-height: unset !important;
  }

  /* Reduce pagination footer height */
  footer {
    font-size: 0.6875rem;
  }
`

export enum ScatterplotSidebarView {
  SETTINGS = 'settings',
  ADD = 'add',
  LABELS = 'labels',
  AGENT = 'agent',
  SENTINELS = 'sentinels',
  EXPERIMENT = 'experiment',
  // DERIVED = 'derived', // Hidden until needed
}

/**
 * The Experiment tab hosts config knobs and diagnostic cards, which need
 * more room than the pen/tag lists — default it wider (still resizable).
 */
const EXPERIMENT_DEFAULT_WIDTH_PERCENT = 62

const ALL_SIDEBAR_VIEWS: ScatterplotSidebarView[] = [
  ScatterplotSidebarView.SETTINGS,
  ScatterplotSidebarView.ADD,
  ScatterplotSidebarView.LABELS,
  ScatterplotSidebarView.AGENT,
]

interface ScatterplotSidebarProps {
  tagsOptions: BaseSensorEntry[]
  selectedTags: BaseSensorEntry[]
  onClose: () => void
  setSidebarView: React.Dispatch<React.SetStateAction<ScatterplotSidebarView>>
  sidebarView: ScatterplotSidebarView
  /** Surfaces that re-home some tabs (the workbench) restrict what shows. */
  visibleTabs?: ScatterplotSidebarView[]
  title?: string
}

export const ScatterplotSidebar: React.FC<ScatterplotSidebarProps> = ({
  tagsOptions,
  selectedTags,
  onClose,
  setSidebarView,
  sidebarView: requestedSidebarView,
  visibleTabs = ALL_SIDEBAR_VIEWS,
  title,
}) => {
  const { t } = useLingui()
  const displayTitle = title ?? t`Tag Configuration`

  const sidebarView = visibleTabs.includes(requestedSidebarView)
    ? requestedSidebarView
    : visibleTabs[0]
  const [sidebarWidth, setSidebarWidth] = useState<number | null>(null)
  const sidebarRef = useRef<HTMLDivElement>(null)
  const { hasPermissions } = useGeckoPermissions()
  const showSentinels = hasPermissions([PERMISSION_GECKO_INTERNAL_MEMBER])
  const { canUseModelBuilder } = useModelBuilderAccess()

  const handleWidthChange = (width: number | null) => {
    if (width !== null && sidebarRef.current?.parentElement) {
      const parentWidth = sidebarRef.current.parentElement.offsetWidth
      setSidebarWidth((width / parentWidth) * 100)
    } else {
      setSidebarWidth(null)
    }
  }

  const renderContent = () => {
    switch (sidebarView) {
      case ScatterplotSidebarView.SETTINGS:
        return (
          <ScrollableContent>
            <PensTab
              setSidebarView={
                visibleTabs.includes(ScatterplotSidebarView.ADD)
                  ? setSidebarView
                  : undefined
              }
            />
          </ScrollableContent>
        )
      case ScatterplotSidebarView.ADD:
        return (
          <ScrollableContent>
            <AllTagsTab
              tags={tagsOptions}
              selectedTags={selectedTags}
              hideFilterMode
            />
          </ScrollableContent>
        )
      case ScatterplotSidebarView.LABELS:
        return (
          <ScrollableContent>
            <LabelsTab />
          </ScrollableContent>
        )
      case ScatterplotSidebarView.AGENT:
        if (!canUseModelBuilder) return null
        return (
          <ScrollableContent>
            <AgentTab />
          </ScrollableContent>
        )
      case ScatterplotSidebarView.SENTINELS:
        if (!showSentinels) return null
        return (
          <ScrollableContent>
            <SentinelsTab />
          </ScrollableContent>
        )
      case ScatterplotSidebarView.EXPERIMENT:
        if (!showSentinels) return null
        return (
          <ScrollableContent>
            <ExperimentTab />
          </ScrollableContent>
        )
    }
  }

  const defaultWidth =
    sidebarView === ScatterplotSidebarView.EXPERIMENT
      ? EXPERIMENT_DEFAULT_WIDTH_PERCENT
      : undefined

  return (
    <SidebarContainer ref={sidebarRef} $width={sidebarWidth || defaultWidth}>
      <ResizeHandleComponent
        onWidthChange={handleWidthChange}
        containerRef={sidebarRef}
        side='left'
      />
      <SidebarInner>
        <SidebarContent>
          <SidebarHeader>
            <ChartTooltip label={t`Close`} side='right'>
              <CantileverButton iconOnly ghost onClick={onClose}>
                <StyledX />
              </CantileverButton>
            </ChartTooltip>
            <SidebarTitle>{displayTitle}</SidebarTitle>
          </SidebarHeader>
          {/* A one-tab strip is a dead control; the header title already
              names the surface. */}
          {visibleTabs.length > 1 && (
            <TabStrip>
              {visibleTabs.includes(ScatterplotSidebarView.SETTINGS) && (
                <Tab
                  $isSelected={sidebarView === ScatterplotSidebarView.SETTINGS}
                  onClick={() =>
                    setSidebarView(ScatterplotSidebarView.SETTINGS)
                  }>
                  <Trans>Pens</Trans>
                </Tab>
              )}
              {visibleTabs.includes(ScatterplotSidebarView.ADD) && (
                <Tab
                  $isSelected={sidebarView === ScatterplotSidebarView.ADD}
                  onClick={() => setSidebarView(ScatterplotSidebarView.ADD)}>
                  <Trans>Tag Search</Trans>
                </Tab>
              )}
              {visibleTabs.includes(ScatterplotSidebarView.LABELS) && (
                <Tab
                  $isSelected={sidebarView === ScatterplotSidebarView.LABELS}
                  onClick={() => setSidebarView(ScatterplotSidebarView.LABELS)}>
                  <Trans>Labels</Trans>
                </Tab>
              )}
              {canUseModelBuilder &&
                visibleTabs.includes(ScatterplotSidebarView.AGENT) && (
                  <Tab
                    $isSelected={sidebarView === ScatterplotSidebarView.AGENT}
                    onClick={() =>
                      setSidebarView(ScatterplotSidebarView.AGENT)
                    }>
                    <Trans>Agent</Trans>
                  </Tab>
                )}
              {showSentinels &&
                visibleTabs.includes(ScatterplotSidebarView.SENTINELS) && (
                  <Tab
                    $isSelected={
                      sidebarView === ScatterplotSidebarView.SENTINELS
                    }
                    onClick={() =>
                      setSidebarView(ScatterplotSidebarView.SENTINELS)
                    }>
                    <Trans>Sentinels</Trans>
                  </Tab>
                )}
              {showSentinels &&
                visibleTabs.includes(ScatterplotSidebarView.EXPERIMENT) && (
                  <Tab
                    $isSelected={
                      sidebarView === ScatterplotSidebarView.EXPERIMENT
                    }
                    onClick={() =>
                      setSidebarView(ScatterplotSidebarView.EXPERIMENT)
                    }>
                    <Trans>Experiment</Trans>
                  </Tab>
                )}
            </TabStrip>
          )}
          {renderContent()}
        </SidebarContent>
      </SidebarInner>
    </SidebarContainer>
  )
}
